import { Search } from "@mui/icons-material";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Cookies from "js-cookie";
import get from "lodash/get";
import Moment from "moment";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import service from "../../services/users";
import config from "../../utils/config";
import { rupiah } from "../../utils/currency";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No employee!
    </Typography>
  </Box>
);

export default function Dashboard() {
  const [pageSize, setPageSize] = useState<number>(10);
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const [options, setOptions] = useState<any>({
    keyword: "",
    is_employee: true,
  });

  const navigate = useNavigate();

  const listEmployee = useQuery(
    ["listEmployee", options],
    ({ queryKey }) => {
      return service.getUsers({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  return (
    <MainLayout>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>

      <Breadcrumb
        label={`Halo ${user.name}, selamat datang di Tenaga Kerja Andalan!`}
        breadcrumbs={[{ label: "", href: "/app/dashboard" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={1}>
              <Controller
                name="keyword"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Name Filter"
                    {...field}
                    {...config.onEnter(() => {
                      form.handleSubmit(onHandleSearch)();
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={form.handleSubmit(onHandleSearch)}
                        >
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Box>
      </ProtectedPermissionPage>

      <Box sx={{ mt: 2 }}>
        <DataGrid
          autoHeight
          loading={listEmployee.isLoading}
          getRowId={(record) => get(record, "id")}
          rows={get(listEmployee, "data.data", [])}
          rowCount={get(listEmployee, "data.data", []).length}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 30]}
          pagination
          disableSelectionOnClick
          onPageChange={(newPage) => {
            setOptions((prev: any) => ({ ...prev, page_number: newPage }));
          }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          components={{
            NoRowsOverlay: CustomNoRowOverlay,
          }}
          columns={[
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) =>
                <Stack
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/app/dashboard/employee/${params.id}`)}
                >
                  <Typography>{params.value}</Typography>
                </Stack>
            },
            {
              field: "email",
              headerName: "Email",
              flex: 1,
              filterable: true,
              minWidth: 150,
              renderCell: (params) => <Typography>{params.value}</Typography>,
            },
            {
              field: "salary",
              headerName: "Salary",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => (
                <Typography>{rupiah(params.value)}</Typography>
              ),
            },
            {
              field: "salary_unit",
              headerName: "Monthly/Daily",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => <Typography>{params.value}</Typography>,
            },
            {
              field: "contract_end",
              headerName: "Contract End",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => {
                const endDate = new Date(params.value);
                let nextMonth = new Date();
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                const isWarning = endDate < nextMonth;
                return (
                  <Typography color={isWarning ? 'red' : 'green'}>
                    {Moment(endDate).format("YYYY-MM-DD")} {isWarning ? '!' : ''}
                  </Typography>
                )
              }
            },
            {
              field: "ongoing_negotiation",
              headerName: "New Contract?",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => <Typography>{params.value ? 'In Progress' : 'No Contract'}</Typography>,
            },
          ]}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
            ".MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "#22404a",
              color: "white",
            },
            ".MuiDataGrid-columnHeader": {
              backgroundColor: "#22404a",
              color: "white",
            },
            ".MuiDataGrid-columnSeparator": {
              backgroundColor: "#22404a",
              color: "white",
            },
          }}
        />
      </Box>
    </MainLayout>
  );
}
